import { useNavigate, useLocation } from "react-router";

export function useQueryState<T>(
  name: string,
  defaultValue: T,
  urlEncode: (value: T) => string,
  urlDecode: (value: string) => T | null,
  replaceURL: boolean = false
): [T, (newState: T) => void] {
  const location = useLocation();
  const navigate = useNavigate();

  let value: T;
  const encodedValue = new URLSearchParams(location.search).get(name);
  if (encodedValue === null) {
    value = defaultValue;
  } else {
    value = urlDecode(encodedValue) ?? defaultValue;
  }

  function setState(newState: T) {
    const searchParams = new URLSearchParams(location.search);
    if (newState === value) return;
    searchParams.set(name, urlEncode(newState));
    location.search = "?" + searchParams.toString();

    if (replaceURL) {
      navigate(location, { replace: true });
    } else {
      navigate(location);
    }
  }

  return [value, setState];
}

export function useQueryStateNumber(
  name: string,
  defaultValue: number,
  replaceURL: boolean = false
): [number, (newState: number) => void] {
  function urlEncode(value: number) {
    return value.toString();
  }
  function urlDecode(value: string) {
    const val = Number(value);

    if (Number.isNaN(val) || !Number.isFinite(val)) {
      return null;
    }
    return val;
  }

  return useQueryState(name, defaultValue, urlEncode, urlDecode, replaceURL);
}

export function useQueryStateString(name: string, defaultValue: string, replaceURL: boolean = false) {
  function urlEncode(value: string) {
    return encodeURIComponent(value);
  }

  function urlDecode(value: string) {
    return decodeURIComponent(value);
  }

  return useQueryState(name, defaultValue, urlEncode, urlDecode, replaceURL);
}

export function useQueryStateBoolean(name: string, defaultValue: boolean, replaceURL: boolean = false) {
  function urlEncode(value: boolean) {
    return encodeURIComponent(value);
  }

  function urlDecode(value: string) {
    if (value === "true") {
      return true;
    } else if (value === "false") {
      return false;
    } else {
      return null;
    }
  }

  return useQueryState(name, defaultValue, urlEncode, urlDecode, replaceURL);
}

export function useQueryStateEnum<T>(name: string, defaultValue: T, replaceURL: boolean = false) {
  function urlEncode(value: T) {
    return (value as any).toString();
  }
  function urlDecode(value: string) {
    const val = Number(value);

    if (Number.isNaN(val) || !Number.isFinite(val)) {
      return defaultValue;
    }
    return val as unknown as T;
  }

  return useQueryState(name, defaultValue, urlEncode, urlDecode, replaceURL);
}
